/**
 * HeroShell.tsx - Lightweight Desktop Landing Shell
 *
 * This is the FAST-LOADING shell that renders Phase 1 (landing page)
 * without any heavy platform imports. It loads in ~30KB instead of 400KB+.
 *
 * Features:
 * - Navbar with Join Beta CTA
 * - Background layers (gradient, halftone, vignette, grain)
 * - Animated headline with brand emphasis
 * - Glowing omnibar input (triggers onActivate on interaction)
 * - Suggestion chips
 * - Tagline
 * - Partners & brokers logo tapes
 * - Floating Join Beta button
 *
 * NO IMPORTS FROM:
 * - @/components/platform/* (SimulationProvider, PlatformLayout, etc.)
 * - Any hooks that initialize WebSocket/SSE connections
 * - Any chart libraries
 *
 * @see .planning/website_optimisation/WEBSITE_LOADING_OPTIMIZATION.md
 */

import React, { useRef, useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Lightweight components only - NO platform imports!
import Navbar from "@/shared/components/full-site/Navbar";
import MiDashTealLogo from "@/shared/assets/logos/MiDash_logos-new/teal-logomark-component";
import AnimatedHeaderMorph from "@/marketing/components/full-site/AnimatedHeaderMorph";

// Lazy load Arabic header - only loaded when user has Arabic language selected (~3KB savings for English users)
const AnimatedHeaderArabicMorph = lazy(() => import("@/marketing/components/full-site/AnimatedHeaderArabicMorph"));
import { PartnersLogoTape, BrokerRevolver } from "@/shared/components/full-site/hero";
import { HeroAnimationCanvas } from "@/marketing/components/full-site/HeroAnimationCanvas";

// Styles
import "@/shared/components/full-site/styles/hero-shared.css";
import "@/marketing/components/full-site/styles/hero-immersive.desktop.css";

// Utils
// Lightweight wrapper — keeps full analytics module (with its heavy deps)
// out of the eager bundle. Calls are queued until App.tsx dynamic-loads
// the real analytics, then drained transparently.
import { trackClick, trackEvent } from "@/shared/utils/track";
import { handleTrialCTA } from "@/shared/utils/trialFlow";
import { useToast } from "@/shared/hooks/use-toast";

// Constants
import { SUGGESTION_CHIPS } from "@/shared/components/full-site/constants/heroConstants";
import { goToBuildAgent } from "@/shared/lib/solutionsUrl";

// Rotating placeholders for the input
const PLACEHOLDERS = {
  en: [
    "Ask MiDash anything...",
    "Buy 100 shares of AAPL",
    "What's my portfolio value?",
    "Show me tech stocks under $50",
    "Analyze TSLA earnings",
  ],
  ar: [
    "اسأل ميداش أي شيء...",
    "اشتري 100 سهم من أبل",
    "ما قيمة محفظتي؟",
    "أرني أسهم التكنولوجيا تحت 50 دولار",
    "حلل أرباح تسلا",
  ],
};

// ============================================================================
// TYPES
// ============================================================================

export interface HeroShellProps {
  /** Called when user interacts with input (focus, type, submit) */
  onActivate: (initialValue?: string) => void;
}

// ============================================================================
// BACKGROUND LAYERS COMPONENT
// ============================================================================

function BackgroundLayers() {
  return (
    <>
      <div className="hero-bg-layer hero-bg-base" />
      <div className="hero-bg-layer hero-bg-accent" />
      <div className="hero-bg-layer hero-halftone" />
      <HeroAnimationCanvas />
      <div className="hero-bg-layer hero-vignette" />
      <div className="hero-bg-layer hero-grain" />
      <div className="hero-bg-layer hero-bridge-glow" />
      <div className="hero-bg-layer hero-right-rail" />
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function HeroShell({ onActivate }: HeroShellProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const isArabic = i18n.language === "ar";

  // Debug RTL - remove after verification
  console.log('[HeroShell] RTL Debug:', { isArabic, language: i18n.language });

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  // Rotating placeholder effect
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDERS.en.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const currentPlaceholder = isArabic
    ? PLACEHOLDERS.ar[placeholderIndex]
    : PLACEHOLDERS.en[placeholderIndex];

  // Preload heavy hero components - but only after page is fully loaded and idle
  // Use a longer delay to avoid blocking TBT during initial load
  const preloadTriggered = useRef(false);

  const triggerPreload = useCallback(() => {
    if (preloadTriggered.current) return;
    preloadTriggered.current = true;

    // Only preload the variant we need based on screen size
    if (window.innerWidth < 768) {
      import("@/marketing/components/full-site/HeroSectionImmersive.mobile");
    } else {
      import("@/marketing/components/full-site/HeroSectionImmersive");
    }
  }, []);

  useEffect(() => {
    // Wait for load event + 5 seconds before preloading
    // This ensures TBT is not affected during initial page load
    const handleLoad = () => {
      setTimeout(triggerPreload, 5000);
    };

    if (document.readyState === "complete") {
      setTimeout(triggerPreload, 5000);
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, [triggerPreload]);

  // Suggestion chips from translations
  const suggestionChips = React.useMemo(() => {
    const translated = t("hero.heroAIChat.suggestionChips", { returnObjects: true });
    return Array.isArray(translated) ? translated : SUGGESTION_CHIPS;
  }, [t]);

  // Handlers
  const handleInputFocus = useCallback(() => {
    trackEvent("platform_engage", { trigger: "focus", source: "desktop_shell" });
    onActivate(inputValue);
  }, [inputValue, onActivate]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim()) {
        trackEvent("platform_engage", { trigger: "submit", source: "desktop_shell" });
        onActivate(inputValue.trim());
      }
    }
  }, [inputValue, onActivate]);

  const handleSubmit = useCallback(() => {
    if (inputValue.trim()) {
      trackEvent("platform_engage", { trigger: "submit", source: "desktop_shell" });
      onActivate(inputValue.trim());
    } else {
      // Focus input if empty
      inputRef.current?.focus();
    }
  }, [inputValue, onActivate]);

  const handleChipClick = useCallback((prompt: string) => {
    trackClick(prompt.substring(0, 30), "hero-suggestion-chip");
    onActivate(prompt);
  }, [onActivate]);

  const handleOpenBetaModal = useCallback(() => {
    // Registration closed → Solutions microsite instead of the signup funnel.
    trackClick("Build Your Trading Agent", "hero-shell");
    goToBuildAgent('hero-shell');
  }, []);

  const handleLogin = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  // Preload on hover - user is showing intent to interact
  const handleInputHover = useCallback(() => {
    triggerPreload();
  }, [triggerPreload]);

  return (
    <div
      className="hero-immersive hero-landing"
      dir={isArabic ? "rtl" : "ltr"}
    >
      {/* Navbar */}
      <header className="hero-navbar">
        <Navbar onJoinBeta={handleOpenBetaModal} />
      </header>

      {/* Background layers */}
      <BackgroundLayers />

      {/* Omnibar Container */}
      <div className="omnibar-container">
        {/* Hero Headline */}
        <div className="hero-headline-wrapper">
          {isArabic ? (
            <Suspense fallback={<div style={{ height: '120px' }} />}>
              <AnimatedHeaderArabicMorph />
            </Suspense>
          ) : (
            <AnimatedHeaderMorph />
          )}
        </div>

        {/* Tagline */}
        <div className="hero-tagline" dir={isArabic ? "rtl" : "ltr"}>
          {isArabic
            ? "حقق أفضلية في الأسواق المالية بذكاء اصطناعي متخصص يفهم اللغة الطبيعية"
            : "Gain your edge in financial markets with specialised natural-language AI."
          }
        </div>

        {/* Input */}
        <div className="omnibar-input-wrapper" onMouseEnter={handleInputHover}>
          <div className="omnibar-glow" />
          <div
            className={`omnibar-input-container ${isArabic ? 'rtl-mode' : ''}`}
            onClick={() => inputRef.current?.focus()}
          >
            {/* RTL: reverse order - submit, input, logo */}
            {isArabic ? (
              <>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="omnibar-submit-btn"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ transform: "scaleX(-1)" }}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleInputFocus}
                  placeholder={currentPlaceholder}
                  className="omnibar-textarea"
                  rows={1}
                />
                <div className="omnibar-logo-container flex items-center justify-center shrink-0">
                  <MiDashTealLogo size={32} animateOnHover={true} emphasis="high" />
                </div>
              </>
            ) : (
              <>
                <div className="omnibar-logo-container flex items-center justify-center shrink-0">
                  <MiDashTealLogo size={32} animateOnHover={true} emphasis="high" />
                </div>
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleInputFocus}
                  placeholder={currentPlaceholder}
                  className="omnibar-textarea"
                  rows={1}
                />
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="omnibar-submit-btn"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Suggestion Chips */}
        <div className="suggestion-chips">
          {suggestionChips.map((chip: { label: string; prompt: string }) => (
            <button
              key={chip.label}
              className="chip"
              onClick={() => handleChipClick(chip.prompt)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* ── Free Trial CTA (inside omnibar-container for z-index) ── */}
        <div
          className="flex flex-col items-center gap-2 w-full mt-6"
          dir={isArabic ? "rtl" : "ltr"}
          style={{ position: "relative", zIndex: 50 }}
        >
          <button
            type="button"
            onClick={() =>
              handleTrialCTA({
                source: "hero-shell",
                navigate,
                toast,
                setLoading: setIsStartingTrial,
              })
            }
            disabled={isStartingTrial}
            className="w-full max-w-md font-semibold px-6 py-3 rounded-xl transition-all duration-200 uppercase tracking-wider text-xs hover:brightness-110 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
            style={{
              fontFamily: "var(--font-sans)",
              backgroundImage: "linear-gradient(90deg, var(--pricing-accent-light), var(--pricing-accent), var(--pricing-accent-deep))",
              boxShadow: "var(--shadow-accent-soft)",
              color: "var(--text-on-accent)",
              minHeight: "48px",
            }}
          >
            {isArabic ? "ابدأ تجربتك المجانية — إلغاء في أي وقت" : "Start Free Trial — Cancel Anytime"}
          </button>
          <p
            className="text-xs text-center mt-1"
            style={{ color: "rgba(248, 250, 252, 0.4)" }}
          >
            {isArabic
              ? "تجربة مجانية 7 أيام · إلغاء في أي وقت"
              : "7-day free trial · Cancel anytime"
            }
          </p>
        </div>
      </div>

      {/* Partners & Brokers Section */}
      <div className="partners-brokers-section">
        <PartnersLogoTape />
        <BrokerRevolver />
      </div>

      {/* Floating Join Beta Button */}
      <button
        type="button"
        className="floating-join-beta"
        onClick={handleOpenBetaModal}
      >
        {t("hero.heroAIChat.joinBeta", "Get Started")}
      </button>

    </div>
  );
}

export default HeroShell;
